import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Edit2, Save, X } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-MY", { month: "long", year: "numeric" });
}

function getInitials(name = "") {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", unit: "", street_number: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", unit: user.unit || "", street_number: user.street_number || "" });
  }, [user]);

  async function save(e) {
    e.preventDefault();
    setError(""); setSuccess(""); setSaving(true);
    try {
      await api("/profile", { method: "PUT", data: form });
      await refreshUser();
      setEditing(false);
      setSuccess("Profile updated successfully.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update profile.");
    } finally { setSaving(false); }
  }

  if (!user) return null;

  return (
    <div>
      <div className="section-panel" style={{ maxWidth: 520 }} data-testid="profile-panel">
        <div className="section-head">
          <div className="section-title">My Profile</div>
          {!editing ? (
            <button className="btn" onClick={() => { setEditing(true); setSuccess(""); setError(""); }} data-testid="edit-profile-btn">
              <Edit2 size={12} /> Edit
            </button>
          ) : (
            <button className="btn" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
              <X size={12} /> Cancel
            </button>
          )}
        </div>

        <div style={{ padding: "18px 20px" }}>
          {/* Avatar & Name */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: 12, background: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>
              {getInitials(user.name)}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, fontFamily: "var(--font-heading)" }}>{user.name}</div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>{capitalize(user.role)} {user.unit ? `· House ${user.unit}` : ""}</div>
            </div>
          </div>

          {error   && <div className="auth-error"   data-testid="profile-error">{error}</div>}
          {success && <div className="auth-success" data-testid="profile-success">{success}</div>}

          {editing ? (
            <form onSubmit={save}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required data-testid="profile-name-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-input" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+60 12-345 6789" data-testid="profile-phone-input" />
              </div>
              <div className="form-group">
                <label className="form-label">House Number</label>
                <input className="form-input" value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))} placeholder="e.g. A-12-3" data-testid="profile-unit-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Street Number / Address</label>
                <input className="form-input" value={form.street_number} onChange={e => setForm(f => ({...f, street_number: e.target.value}))} placeholder="e.g. Jalan Mawar" data-testid="profile-street-input" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-profile-btn">
                <Save size={12} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </form>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "10px 16px", fontSize: 12 }}>
              {[
                ["Email",     user.email],
                ["Phone",     user.phone || "—"],
                ["House No.", user.unit  || "—"],
                ["Street",    user.street_number || "—"],
                ["Role",      capitalize(user.role)],
                ["Status",    <span className={`badge ${user.status}`}>{capitalize(user.status)}</span>],
                ["Joined",    fmtDate(user.created_at)],
              ].map(([label, val]) => (
                <>
                  <span style={{ color: "var(--text2)", alignSelf: "center" }} key={`l-${label}`}>{label}</span>
                  <span style={{ alignSelf: "center", fontFamily: label === "House No." ? "var(--mono)" : "inherit", fontSize: label === "House No." ? 11 : 12 }} key={`v-${label}`} data-testid={`profile-${label.toLowerCase().replace(/\s+/g,"-")}`}>{val}</span>
                </>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }
